import React, { useState, useEffect } from "react";
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import { useNavigate } from "react-router-dom";
import { searchInvoices, sendCreditNoteToPeppol } from "../services/creditNoteService";
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';
import { useTranslation } from "react-i18next";
import { useStoreInfo } from "../../../hooks/useCompanyInfo";
import Handlebars from "handlebars";
import html2pdf from "html2pdf.js";
import {
  Box,
  Button,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Grid,
  TextField,
  Container,
  Stack,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import PreviewIcon from "@mui/icons-material/Preview";
import ReceiptIcon from "@mui/icons-material/Receipt";
import DownloadIcon from "@mui/icons-material/Download";

function AddCreditNotePage() {
  const { t } = useTranslation();
  const { storeInfo, loading: storeInfoLoading } = useStoreInfo();
  const navigate = useNavigate();
  
  // State
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const blocker = useUnsavedChanges(selectedInvoice !== null);
  const [creditType, setCreditType] = useState("Full");
  const [selectedLineItems, setSelectedLineItems] = useState([]);
  const [lineItemQuantities, setLineItemQuantities] = useState({});
  const [reason, setReason] = useState("");
  const [creditNoteNumber, setCreditNoteNumber] = useState("");
  const [creditNoteTemplates, setCreditNoteTemplates] = useState([]);
  const [selectedCreditNoteTemplate, setSelectedCreditNoteTemplate] = useState('');

  useEffect(() => {
    // Skip if templates already loaded
    if (creditNoteTemplates.length > 0) return;
    
    documentNoteTemplateService.getTemplatesByType('CreditNote')
      .then((data) => {
        const allTemplates = data?.payload || data || [];
        // Filter templates by DocumentType.CreditNote (5)
        const templates = allTemplates.filter(t => 
          t.DocumentType === 5 || // DocumentType.CreditNote
          t.DocumentType === 99   // DocumentType.All
        );
        setCreditNoteTemplates(templates);
        // Auto-select default or first template and populate reason
        if (templates.length > 0) {
          const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
          setSelectedCreditNoteTemplate(defaultTemplate.Id);
          const plainText = defaultTemplate.NoteContent?.replace(/<[^>]*>/g, '') || '';
          setReason(plainText.slice(0, 1000));
        }
      })
      .catch(() => {});
  }, []);

  const handleCreditNoteTemplateSelect = (id) => {
    setSelectedCreditNoteTemplate(id);
    if (!id) return;
    const tmpl = creditNoteTemplates.find((t) => t.Id === id);
    if (tmpl) setReason(tmpl.NoteContent.replace(/<[^>]*>/g, '').slice(0, 1000));
  };
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Generate credit note number when invoice is selected
  useEffect(() => {
    if (selectedInvoice) {
      
      setCreditNoteNumber(`CN-${selectedInvoice.InvoiceNumber}`);
      
      // Initialize all line items as selected for Full credit
      if (creditType === "Full" && selectedInvoice.LineItems) {
        setSelectedLineItems(selectedInvoice.LineItems.map((_, idx) => idx));
        const quantities = {};
        selectedInvoice.LineItems.forEach((item, idx) => {
          quantities[idx] = item.Quantity;
        });
        setLineItemQuantities(quantities);
      }
    }
  }, [selectedInvoice]);

  // Reset line items when credit type changes
  useEffect(() => {
    if (selectedInvoice && selectedInvoice.LineItems) {
      if (creditType === "Full") {
        setSelectedLineItems(selectedInvoice.LineItems.map((_, idx) => idx));
        const quantities = {};
        selectedInvoice.LineItems.forEach((item, idx) => {
          quantities[idx] = item.Quantity;
        });
        setLineItemQuantities(quantities);
      } else {
        setSelectedLineItems([]);
        setLineItemQuantities({});
      }
    }
  }, [creditType, selectedInvoice]);

  const handleInvoiceSearch = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      setInvoiceOptions([]);
      return;
    }
    
    try {
      setLoadingInvoices(true);
      const response = await searchInvoices(searchTerm);
      
      
      // Handle response structure
      let invoices = [];
      if (response.status === 'Success' && response.payload) {
        invoices = Array.isArray(response.payload) ? response.payload : [response.payload];
      } else if (response.status && response.data) {
        // Handle {status, data} structure
        invoices = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        invoices = response;
      } else if (response.data) {
        // Handle case where response might have a data property
        invoices = Array.isArray(response.data) ? response.data : [response.data];
      } else if (typeof response === 'object' && response !== null) {
        // If response is an object but not an array, wrap it
        invoices = [response];
      }
      
      
      if (invoices.length > 0) {
      }
      
      // Show all invoices regardless of Peppol status
      // Users can create credit notes for any invoice
      
      setInvoiceOptions(invoices);
    } catch (err) {
      setInvoiceOptions([]);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleLineItemToggle = (index) => {
    if (selectedLineItems.includes(index)) {
      setSelectedLineItems(selectedLineItems.filter(i => i !== index));
      const newQuantities = { ...lineItemQuantities };
      delete newQuantities[index];
      setLineItemQuantities(newQuantities);
    } else {
      setSelectedLineItems([...selectedLineItems, index]);
      setLineItemQuantities({
        ...lineItemQuantities,
        [index]: selectedInvoice.LineItems[index].Quantity
      });
    }
  };

  const handleQuantityChange = (index, value) => {
    const maxQty = selectedInvoice.LineItems[index].Quantity;
    const qty = Math.min(Math.max(0, parseFloat(value) || 0), maxQty);
    setLineItemQuantities({
      ...lineItemQuantities,
      [index]: qty
    });
  };

  const calculateTotals = () => {
    if (!selectedInvoice || !selectedInvoice.LineItems) {
      return { subTotal: '0.00', vatAmount: '0.00', totalAmount: '0.00', vatBreakdown: [] };
    }

    // For Full credit, use invoice totals directly
    if (creditType === "Full") {
      
      const invoiceSubTotal = selectedInvoice.InvoiceSubTotal || selectedInvoice.InvoiceSubTotalAmount || 0;
      const invoiceVATAmount = selectedInvoice.InvoiceTotalVATAmount || 0;
      const invoiceTotalAmount = selectedInvoice.TotalAmount || selectedInvoice.InvoiceTotalAmount || 0;
      
      
      return {
        subTotal: invoiceSubTotal.toFixed(2),
        vatAmount: invoiceVATAmount.toFixed(2),
        totalAmount: invoiceTotalAmount.toFixed(2),
        vatBreakdown: []
      };
    }

    // For Partial credit, calculate from selected line items
    let subTotal = 0;
    let vatAmount = 0;
    const vatByRate = {};

    selectedLineItems.forEach(index => {
      const item = selectedInvoice.LineItems[index];
      const qty = parseFloat(lineItemQuantities[index] || 0);
      const unitPrice = parseFloat(item.UnitPrice || item.Price || 0);
      const vatPercentage = parseFloat(item.VATPercentage || 0);
      
      const itemTotal = qty * unitPrice;
      const itemVat = (itemTotal * vatPercentage) / 100;
      
      subTotal += itemTotal;
      vatAmount += itemVat;
      
      // Group by VAT rate
      if (!vatByRate[vatPercentage]) {
        vatByRate[vatPercentage] = 0;
      }
      vatByRate[vatPercentage] += itemVat;
    });

    const totalAmount = subTotal + vatAmount;
    
    // Convert vatByRate to array for template
    const vatBreakdown = Object.entries(vatByRate).map(([rate, amount]) => ({
      rate: rate,
      amount: amount.toFixed(2)
    }));

    const result = {
      subTotal: subTotal.toFixed(2),
      vatAmount: vatAmount.toFixed(2),
      totalAmount: totalAmount.toFixed(2),
      vatBreakdown
    };
    
    return result;
  };

  const loadCreditNoteTemplate = async () => {
    // Get current language directly from i18next without triggering re-renders
    const currentLang = localStorage.getItem('i18nextLng') || 'en';
    const lang = currentLang.split('-')[0]; // Handle cases like 'en-US'
    const templatePath = `/creditnotetemplates/creditnote-${lang}.html`;
    
    try {
      const response = await fetch(templatePath);
      if (!response.ok) throw new Error('Template not found');
      return await response.text();
    } catch (error) {
      // Fallback to English
      const fallbackResponse = await fetch('/creditnotetemplates/creditnote-en.html');
      return await fallbackResponse.text();
    }
  };

  const renderCreditNoteTemplate = async () => {
    if (!selectedInvoice) return null;

    const template = await loadCreditNoteTemplate();
    const compiledTemplate = Handlebars.compile(template);

    const totals = calculateTotals();

    // Extract customer data from Customer object
    const customer = selectedInvoice.Customer || {};
    const customerName = customer.Name || customer.CompanyName || selectedInvoice.CustomerName || "";
    const customerVAT = customer.VATNumber || selectedInvoice.CustomerVAT || "";
    const customerAddress = customer.Street || selectedInvoice.CustomerAddress || "";
    const customerCity = customer.City || selectedInvoice.CustomerCity || "";
    const customerPostCode = customer.PostCode || selectedInvoice.CustomerPostalCode || "";
    const customerCountry = customer.Country || selectedInvoice.CustomerCountry || "";

    // Format date helper
    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    };

    // Get invoice date from invoice CreatedDate
    const invoiceDate = selectedInvoice.CreatedDate || selectedInvoice.InvoiceDate || new Date().toISOString();

    const lineItems = selectedLineItems.map(index => {
      const item = selectedInvoice.LineItems[index];
      const qty = lineItemQuantities[index] || 0;
      const originalQty = parseFloat(item.Quantity || 1);
      const unitPrice = parseFloat(item.UnitPrice || item.Price || 0);
      const totalExcl = (qty * unitPrice);
      const vatPercentage = parseFloat(item.VATPercentage || 0);
      
      // Use TotalVAT from line item if available, calculate proportionally for partial quantities
      let vatAmount;
      if (item.TotalVAT !== undefined && item.TotalVAT !== null) {
        // Use proportional VAT based on credited quantity
        vatAmount = (qty / originalQty) * parseFloat(item.TotalVAT);
      } else {
        // Fallback to calculation if TotalVAT not available
        vatAmount = (totalExcl * vatPercentage) / 100;
      }
      
      const totalIncl = totalExcl + vatAmount;
      
      return {
        UpdatedProductName: item.UpdatedProductName || item.updatedProductName || item.ProductName || item.Description || item.Name || "",
        Quantity: qty,
        UnitPrice: unitPrice.toFixed(2),
        VATPercentage: vatPercentage,
        TotalExcl: totalExcl.toFixed(2),
        VATAmount: vatAmount.toFixed(2),
        TotalIncl: totalIncl.toFixed(2)
      };
    });

    // Log storeInfo for debugging
    
    const data = {
      // Company/Store Info - from store info hook
      CompanyName: storeInfo?.name || "",
      CompanyVAT: storeInfo?.vat || "",
      CompanyStreetAddress: storeInfo?.address || "",
      CompanyCityCountry: [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "",
      CompanyIBAN: storeInfo?.iban || "",
      
      // Customer Info - from Customer object
      CustomerName: customerName,
      CustomerVAT: customerVAT,
      CustomerStreetAddress: customerAddress,
      CustomerCityCountry: [customerPostCode, customerCity, customerCountry].filter(Boolean).join(' '),
      
      // Credit Note Info
      CreditNoteNumber: creditNoteNumber,
      CreditNoteDate: new Date().toLocaleDateString(),
      CreditNoteReason: reason,
      CreditType: creditType,
      TotalQuantity: selectedLineItems.length,
      
      // Original Invoice Reference
      OriginalInvoiceNumber: selectedInvoice.InvoiceNumber || "",
      InvoiceDate: formatDate(invoiceDate),
      OriginalInvoiceAmount: selectedInvoice.TotalAmount?.toFixed(2) || selectedInvoice.InvoiceTotalAmount?.toFixed(2) || "0.00",
      InvoiceTotalVATAmount: selectedInvoice.InvoiceTotalVATAmount?.toFixed(2) || "0.00",
      
      // Line Items
      LineItems: lineItems,
      
      // Totals
      SubTotal: totals.subTotal,
      VATAmount: totals.vatAmount,
      TotalAmount: totals.totalAmount,
      VATBreakdown: totals.vatBreakdown,
  
      // Translations
      T_CREDIT_NOTE: t('credit_note_title', 'CREDIT NOTE'),
      T_CREDIT_TO: t('credit_to', 'Credit To'),
      T_BILLED_TO: t('billed_to', 'Billed to'),
      T_VAT: t('vat', 'VAT'),
      T_ADDRESS: t('address', 'Address'),
      T_IBAN: t('iban', 'IBAN'),
      T_CREDIT_NOTE_DATE: t('credit_note_date', 'Credit Note Date'),
      T_ORIGINAL_INVOICE: t('original_invoice', 'Original Invoice'),
      T_INVOICE_DATE: t('invoice_date', 'Invoice Date'),
      T_REASON_FOR_CREDIT_NOTE: t('reason_for_credit_note', 'Reason for Credit Note'),
      T_DESCRIPTION: t('description', 'Description'),
      T_QTY: t('qty', 'Qty'),
      T_UNIT_PRICE: t('unit_price', 'Unit Price'),
      T_NET_TOTAL: t('net_total', 'Net Total'),
      T_VAT_AMOUNT: t('vat_amount', 'VAT Amount'),
      T_SUB_TOTAL: t('sub_total', 'Sub Total'),
      T_SUBTOTAL_EXCL_VAT: t('subtotal_excl_vat', 'Subtotal (Excl. VAT)'),
      T_TOTAL_CREDIT: t('total_credit', 'TOTAL CREDIT'),
      T_NOTES: t('notes', 'Notes'),
      T_THANK_YOU: t('thank_you_for_business', 'Thank you for your business!'),
      T_PAGE: t('page', 'Page'),
      T_OF: t('of', 'of'),
      T_INVOICE_VAT_AMOUNT: t('invoice_vat_amount', 'Invoice VAT Amount'),
      
      // Optional
      CustomText: "",
      GeneratedDate: new Date().toLocaleString(),
      PageNumber: 1,
      TotalPages: 1
    };


    return compiledTemplate(data);
  };

  const handlePreview = async () => {
    // Check if storeInfo is loaded
    if (!storeInfo) {
      setError("Loading company information... Please try again in a moment.");
      return;
    }
    
    try {
      setLoading(true);
      setError("");
      const html = await renderCreditNoteTemplate();
      setPreviewHtml(html);
      setPreviewDialogOpen(true);
    } catch (err) {
      setError("Failed to generate preview");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPreview = async () => {
    if (!previewHtml) return;
    
    try {
      setLoading(true);
      
      // Generate PDF from the preview HTML
      const pdfBlob = await generatePDF(previewHtml);
      
      // Create download link
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${creditNoteNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (err) {
      setError("Failed to download PDF");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async (html) => {
    const opt = {
      margin: 0,
      filename: `${creditNoteNumber}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, 
        useCORS: true,
        logging: false,
        width: 794,
        windowWidth: 794,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: ['tr', 'td', 'img']
      }
    };
    
    // Generate PDF blob - same method as invoice
    const pdf = html2pdf().set(opt).from(html);
    const pdfBlob = await pdf.output('blob');
    
    
    return pdfBlob;
  };

  const handleSubmit = async () => {
    // Validation
    if (!selectedInvoice) {
      setError("Please select a Peppol invoice");
      return;
    }
    if (!storeInfo) {
      setError("Loading company information... Please try again in a moment.");
      return;
    }
    if (!reason.trim()) {
      setError("Please provide a reason for the credit note");
      return;
    }
    if (selectedLineItems.length === 0) {
      setError("Please select at least one line item");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      // Generate PDF
      const html = await renderCreditNoteTemplate();
      const pdfBlob = await generatePDF(html);

      // Prepare FormData
      const formData = new FormData();
      
      // Add all required fields with proper formatting
      formData.append('InvoiceId', selectedInvoice.Id);
      formData.append('CreditNoteNumber', creditNoteNumber);
      formData.append('CreditNoteDate', new Date().toISOString());
      formData.append('Reason', reason);
      formData.append('CreditType', creditType); // "Full" or "Partial"
      formData.append('IncludeAttachments', true);

      // Add line items for Partial credit notes only
      if (creditType === "Partial" && selectedLineItems.length > 0) {
        selectedLineItems.forEach((index, idx) => {
          const item = selectedInvoice.LineItems[index];
          const qty = lineItemQuantities[index];
          
          formData.append(`LineItems[${idx}].ProductId`, item.ProductId || 0);
          formData.append(`LineItems[${idx}].Description`, item.UpdatedProductName || item.updatedProductName || item.Description || item.ProductName || '');
          formData.append(`LineItems[${idx}].Quantity`, qty);
          formData.append(`LineItems[${idx}].UnitPrice`, item.UnitPrice || 0);
          formData.append(`LineItems[${idx}].VATPercentage`, item.VATPercentage || 0);
        });
      }

      // Add PDF attachment (must be last)
      formData.append('PdfAttachment', pdfBlob, `${creditNoteNumber}.pdf`);
      
      // Log FormData for debugging
      for (let [key, value] of formData.entries()) {
      }

      // Submit
      await sendCreditNoteToPeppol(formData);

      setSuccess("Credit note sent to Peppol successfully!");
      
      // Redirect to list page after short delay
      setTimeout(() => {
        navigate('/creditnotes');
      }, 1500);

    } catch (err) {
      setError(err.response?.data?.message || "Failed to send credit note");
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Box sx={{
      background: 'linear-gradient(to bottom, #f8fafc 0%, #f1f5f9 100%)',
      minHeight: '100vh',
      pt: 3,
      pb: 8
    }}>
      <Container maxWidth="lg">
        {error && (
          <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Card elevation={3} sx={{ borderRadius: '16px', overflow: 'hidden', mb: 4 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ReceiptIcon sx={{ mr: 2, fontSize: 32, color: '#c62828' }} />
                <Typography variant="h5" fontWeight="bold">
                  {t("create_credit_note") || "Create Credit Note"}
                </Typography>
              </Box>
            }
            sx={{ bgcolor: '#ffebee' }}
          />

          <CardContent>
            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} md={8}>
                <Stack spacing={3}>
                  {/* Invoice Search */}
                  <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                      {t("search_invoice") || "Search Invoice"}
                    </Typography>
                    {/* Debug info */}
                    {invoiceOptions.length > 0 && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        Found {invoiceOptions.length} invoice(s)
                      </Alert>
                    )}
                    <Autocomplete
                      options={invoiceOptions}
                      getOptionLabel={(option) => {
                        const invoiceNum = option.InvoiceNumber || 'N/A';
                        const customer = option.CustomerName || 'Unknown';
                        const total = option.InvoiceTotalAmount || option.TotalAmount || 0;
                        const label = `${invoiceNum} - ${customer} - €${total.toFixed(2)}`;
                        return label;
                      }}
                      value={selectedInvoice}
                      onChange={(e, value) => {
                        setSelectedInvoice(value);
                      }}
                      onInputChange={(e, value, reason) => {
                        setSearchInput(value);
                        if (reason === 'input') {
                          handleInvoiceSearch(value);
                        }
                      }}
                      loading={loadingInvoices}
                      filterOptions={(x) => {
                        return x;
                      }}
                      noOptionsText={
                        searchInput.length < 3 
                          ? "Type at least 3 characters to search"
                          : loadingInvoices 
                            ? "Searching..."
                            : "No invoices found"
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("select_invoice") || "Select Invoice"}
                          placeholder={t("search_by_number_customer") || "Type invoice number (min 3 chars)"}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingInvoices ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Paper>

                  {/* Invoice Details */}
                  {selectedInvoice && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff3e0' }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        {t("invoice_details") || "Invoice Details"}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("invoice_number") || "Invoice Number"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedInvoice.InvoiceNumber}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("invoice_date") || "Invoice Date"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedInvoice.CreatedDate
                              ? new Date(selectedInvoice.CreatedDate).toLocaleDateString()
                              : selectedInvoice.InvoiceDate ? new Date(selectedInvoice.InvoiceDate).toLocaleDateString() : 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("customer") || "Customer"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedInvoice.Customer?.Name || selectedInvoice.Customer?.CompanyName || selectedInvoice.CustomerName}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("customer_vat") || "Customer VAT"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedInvoice.Customer?.VATNumber || selectedInvoice.CustomerVAT || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("total_products") || "Total Products"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedInvoice.LineItems?.length || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            {t("total_amount") || "Total Amount"}
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary">
                            €{selectedInvoice.TotalAmount?.toFixed(2) || selectedInvoice.InvoiceTotalAmount?.toFixed(2) || '0.00'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>
                  )}

                  {/* Credit Type Selection */}
                  {selectedInvoice && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">
                          <Typography variant="h6">
                            {t("credit_type") || "Credit Type"}
                          </Typography>
                        </FormLabel>
                        <RadioGroup
                          row
                          value={creditType}
                          onChange={(e) => setCreditType(e.target.value)}
                        >
                          <FormControlLabel
                            value="Full"
                            control={<Radio />}
                            label={t("full_credit") || "Full Credit"}
                          />
                          <FormControlLabel
                            value="Partial"
                            control={<Radio />}
                            label={t("partial_credit") || "Partial Credit"}
                            disabled={true}
                          />
                        </RadioGroup>
                      </FormControl>
                    </Paper>
                  )}

                  {/* Line Items Selection */}
                  {selectedInvoice && selectedInvoice.LineItems && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {t("line_items") || "Line Items"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {creditType === "Full" 
                          ? t("all_items_selected") || "All items will be credited"
                          : t("select_items_to_credit") || "Select items to credit"}
                      </Typography>
                      
                      <Table size="small" sx={{ mt: 2 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            {creditType === "Partial" && <TableCell padding="checkbox"></TableCell>}
                            <TableCell>{t("description") || "Description"}</TableCell>
                            <TableCell align="center">{t("original_qty") || "Original Qty"}</TableCell>
                            <TableCell align="center">{t("credit_qty") || "Credit Qty"}</TableCell>
                            <TableCell align="right">{t("unit_price") || "Unit Price"}</TableCell>
                            <TableCell align="right">{t("vat") || "VAT"}</TableCell>
                            <TableCell align="right">{t("total") || "Total"}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedInvoice.LineItems.map((item, index) => {
                            const isSelected = selectedLineItems.includes(index);
                            const qty = lineItemQuantities[index] || 0;
                            const originalQty = parseFloat(item.Quantity || 1);
                            const unitPriceAmount = qty * item.UnitPrice;
                            
                            // Calculate VAT using TotalVAT field if available
                            let vatAmount = 0;
                            if (isSelected) {
                              if (item.TotalVAT !== undefined && item.TotalVAT !== null) {
                                vatAmount = ((qty / originalQty) * parseFloat(item.TotalVAT));
                              } else {
                                vatAmount = (unitPriceAmount * item.VATPercentage) / 100;
                              }
                            }
                            
                            // Total = Unit Price + VAT
                            const total = (unitPriceAmount + vatAmount).toFixed(2);
                            const vatAmountFormatted = vatAmount.toFixed(2);

                            return (
                              <TableRow key={index} hover={creditType === "Partial"}>
                                {creditType === "Partial" && (
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleLineItemToggle(index)}
                                    />
                                  </TableCell>
                                )}
                                <TableCell>{item.UpdatedProductName || item.updatedProductName || item.Description || item.ProductName}</TableCell>
                                <TableCell align="center">{item.Quantity}</TableCell>
                                <TableCell align="center">
                                  {isSelected ? (
                                    <TextField
                                      type="number"
                                      size="small"
                                      value={qty}
                                      onChange={(e) => handleQuantityChange(index, e.target.value)}
                                      disabled={creditType === "Full"}
                                      inputProps={{ 
                                        min: 0, 
                                        max: item.Quantity,
                                        step: 0.01,
                                        style: { textAlign: 'center', width: '70px' }
                                      }}
                                    />
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                                <TableCell align="right">€{item.UnitPrice?.toFixed(2)}</TableCell>
                                <TableCell align="right">
                                  {isSelected ? `€${vatAmountFormatted}` : '-'}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isSelected ? '#c62828' : 'inherit', fontWeight: isSelected ? 'bold' : 'normal' }}>
                                  {isSelected ? `€${total}` : '-'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}

                  {/* Reason */}
                  {selectedInvoice && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {t("reason_for_credit") || "Reason for Credit"}
                      </Typography>
                      {creditNoteTemplates.length > 0 && (
                        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                          <InputLabel>{t('select_template') || 'Select template'}</InputLabel>
                          <Select
                            value={selectedCreditNoteTemplate}
                            label={t('select_template') || 'Select template'}
                            onChange={(e) => handleCreditNoteTemplateSelect(e.target.value)}
                          >
                            <MenuItem value=""><em>{t('none') || 'None'}</em></MenuItem>
                            {creditNoteTemplates.map((tmpl) => (
                              <MenuItem key={tmpl.Id} value={tmpl.Id}>{tmpl.TemplateName}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={t("enter_reason") || "Enter reason for issuing this credit note..."}
                        required
                        inputProps={{ maxLength: 1000 }}
                        helperText={`${reason.length}/1000`}
                      />
                    </Paper>
                  )}
                </Stack>
              </Grid>

              {/* Right Column - Summary */}
              <Grid item xs={12} md={4}>
                <Stack spacing={3}>
                  {/* Credit Note Number */}
                  {selectedInvoice && (
                    <Paper elevation={3} sx={{ p: 2, borderRadius: 2, bgcolor: '#c62828', color: 'white' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {t("credit_note_number") || "Credit Note Number"}
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {creditNoteNumber}
                      </Typography>
                    </Paper>
                  )}

                  {/* Totals */}
                  {selectedInvoice && selectedLineItems.length > 0 && (
                    <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        {t("credit_totals") || "Credit Totals"}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body1" fontWeight="bold">{t("sub_total") || "Sub Total (Excl. VAT)"}:</Typography>
                          <Typography variant="body1" color="#c62828" fontWeight="bold">
                            -€{totals.subTotal}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">{t("vat_amount") || "VAT Amount"}:</Typography>
                          <Typography variant="body2" color="#c62828" fontWeight="bold">
                            -€{totals.vatAmount}
                          </Typography>
                        </Box>
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="h6">{t("total_credit") || "Total Credit"}:</Typography>
                          <Typography variant="h6" color="#c62828" fontWeight="bold">
                            -€{totals.totalAmount}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  )}

                  {/* Action Buttons */}
                  {selectedInvoice && (
                    <Stack spacing={2}>
                      <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<PreviewIcon />}
                        onClick={handlePreview}
                        disabled={loading || storeInfoLoading || !storeInfo || selectedLineItems.length === 0 || !reason.trim()}
                      >
                        {storeInfoLoading ? "Loading..." : (t("preview") || "Preview")}
                      </Button>
                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<SendIcon />}
                        onClick={handleSubmit}
                        disabled={loading || storeInfoLoading || !storeInfo || selectedLineItems.length === 0 || !reason.trim()}
                        sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#8b1f1f' } }}
                      >
                        {loading ? <CircularProgress size={24} /> : storeInfoLoading ? "Loading..." : (t("send_to_peppol") || "Send to Peppol")}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {t("credit_note_preview") || "Credit Note Preview"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '600px'
              }}
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewDialogOpen(false)}>
              {t("close") || "Close"}
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPreview}
              disabled={loading}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#115293' } }}
            >
              {loading ? <CircularProgress size={20} /> : (t("download_pdf") || "Download PDF")}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </Box>
  );
}

export default AddCreditNotePage;
